import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Flex, Form, FormInstance, InputNumber, Modal, Row, Select, Space, Table } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import { ColumnsType } from 'antd/es/table';
import withAuthorization from 'components/hocs/withAuthorization';
import { PageWithFiltersContentContainer } from 'components/page/page.styles';
import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import useCreateCurrencyExchange from 'hooks/data/useCreateCurrencyExchange';
import useGetCurrencyExchange from 'hooks/data/useGetCurrencyExchange';
import useToastContext from 'hooks/useToastContext';
import { Currency } from 'interfaces/currencies';
import React, { useState } from 'react';
import PageWithFilters from 'components/page/page-with-filters';
import { CurrencyCode, RealmRoles } from '../../constants';

const { RangePicker } = DatePicker;

const filterOption = (input: string, option?: { label: string; value: string }) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

type NewCurrencyExchangeFormInputs = {
  base: CurrencyCode;
  target: CurrencyCode;
  rate: number;
  starts: Dayjs;
};

type CurrencyExchangesFilterProps = {
  rangeDates: [Dayjs, Dayjs];
};

const CurrencyExchangesFilter = ({ onSubmit }: { onSubmit: (data: CurrencyExchangesFilterProps) => void }) => {
  const [form] = Form.useForm<CurrencyExchangesFilterProps>();

  return (
    <Form form={form} onFinish={onSubmit}>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="rangeDates">
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}></Col>
        <Col span={8} style={{ textAlign: 'right' }}>
          <Space>
            <Button htmlType="reset">Limpiar</Button>
            <Button htmlType="submit" type="primary">
              Buscar
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

const SubmitButton = ({ form, loading }: { form: FormInstance; loading: boolean }) => {
  const [submittable, setSubmittable] = React.useState(false);

  // Watch all values
  const values = Form.useWatch([], form);

  React.useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      }
    );
  }, [values]);

  return (
    <Button type="primary" htmlType="submit" disabled={!submittable} loading={loading}>
      Crear
    </Button>
  );
};

const NewCurrencyExchangeForm = ({ onCancel }: { onCancel: () => void }) => {
  const toast = useToastContext();
  const [form] = Form.useForm();

  const { createCurrencyExchange, isLoading: createCurrencyExchangeLoading } = useCreateCurrencyExchange();

  const onSubmit = async (data: NewCurrencyExchangeFormInputs) => {
    try {
      const res = await createCurrencyExchange({
        ...data
      });
      if (res.status === 200) {
        toast.success('Exito', 'Se ha creado el nuevo currency exchange con exito.');
        form.resetFields();
        onCancel();
      } else {
        throw new Error();
      }
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Intente nuevamente');
    }
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current: Dayjs) => {
    const yesterday = dayjs().add(-1, 'day');
    const after2days = dayjs().add(2, 'day');
    return !current.isAfter(yesterday) || !current.isBefore(after2days);
  };

  return (
    <Form<NewCurrencyExchangeFormInputs>
      name="basic"
      form={form}
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      style={{ maxWidth: '100%', marginTop: '1rem' }}
      initialValues={{ remember: true, starts: dayjs() }}
      onFinish={onSubmit}
      onFinishFailed={console.log}
      autoComplete="off"
    >
      <Form.Item<NewCurrencyExchangeFormInputs>
        label="De"
        name="base"
        validateTrigger="onBlur"
        rules={[{ required: true, message: 'Por favor selecciona una opcion.' }]}
      >
        <Select
          placeholder="Seleccionar"
          showSearch
          filterOption={filterOption}
          options={Object.keys(CurrencyCode).map((code) => ({ label: code, value: code }))}
        />
      </Form.Item>

      <Form.Item<NewCurrencyExchangeFormInputs>
        label="A"
        name="target"
        validateTrigger="onBlur"
        rules={[{ required: true, message: 'Por favor selecciona una opcion.' }]}
      >
        <Select
          showSearch
          placeholder="Seleccionar"
          filterOption={filterOption}
          options={Object.keys(CurrencyCode).map((code) => ({ label: code, value: code }))}
        />
      </Form.Item>

      <Form.Item<NewCurrencyExchangeFormInputs>
        label="Tasa"
        name="rate"
        validateTrigger="onBlur"
        rules={[
          { required: true, message: 'Por favor ingresar un valor de tasa.' },
          { type: 'number', message: 'Ingrese un numero por favor.' },
        ]}
      >
        <InputNumber controls={false} />
      </Form.Item>

      <Form.Item<NewCurrencyExchangeFormInputs>
        label="Fecha de Inicio"
        name="starts"
        validateTrigger="onBlur"
        rules={[{ required: true, message: 'Por favor selecciona una opcion.' }]}
      >
        <DatePicker style={{ width: '100%' }} disabledDate={disabledDate} format="YYYY-MM-DDTHH:mm:ss-03:00" showTime={true}/>
      </Form.Item>

      <Form.Item wrapperCol={{ offset: 8, span: 16 }} style={{ marginTop: '1rem' }}>
        <Flex justify="flex-end">
          <Space>
            <Button danger htmlType="reset" onClick={() => onCancel()}>
              Cancelar
            </Button>
            <SubmitButton form={form} loading={createCurrencyExchangeLoading} />
          </Space>
        </Flex>
      </Form.Item>
    </Form>
  );
};

const CurrencyExchange = () => {
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  const [newCurrencyExchangeModalOpen, setNewCurrencyExchangeModalOpen] = useState<boolean>(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 1,
    pageSize: 10,
  });

  const { currencyExchange, pageInfo, isLoading } = useGetCurrencyExchange(
    paginationModel.page,
    paginationModel.pageSize,
    filters.startDate,
    filters.endDate
  );

  const onSearchClick = (data: CurrencyExchangesFilterProps) => {
    setFilters({
      startDate: data.rangeDates?.[0] ? `${format(data.rangeDates?.[0]?.toDate(), 'yyyy-MM-dd')}T00:00:00` : '',
      endDate: data.rangeDates?.[1] ? `${format(data.rangeDates?.[1]?.toDate(), 'yyyy-MM-dd')}T00:00:00` : '',
    });
  };

  const columns: ColumnsType<Currency> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'ID',
    },
    {
      dataIndex: 'from',
      key: 'from',
      title: 'De',
    },
    {
      dataIndex: 'to',
      key: 'to',
      title: 'A',
    },
    {
      dataIndex: 'rate',
      key: 'rate',
      title: 'Tasa',
    },
    {
      dataIndex: 'starts',
      key: 'starts',
      title: 'Fecha de Inicio',
      render: (_, record) => format(new Date(record?.starts), 'dd-MM-yyyy HH:mm'),
    },
    {
      dataIndex: 'created',
      key: 'created',
      title: 'Fecha de Creacion',
      render: (_, record) => format(new Date(record?.created), 'dd-MM-yyyy HH:mm'),
    },
  ];

  const NewCurrencyExchangeBtn = withAuthorization([RealmRoles.CurrencyExchangeAllWrite], () => (
    <Button icon={<PlusOutlined />} onClick={() => setNewCurrencyExchangeModalOpen(true)} type="primary">
      Nuevo
    </Button>
  ));

  return (
    <PageWithFilters title="Cambio de Divisas">
      <PageWithFiltersContentContainer>
        <CurrencyExchangesFilter onSubmit={onSearchClick} />
      </PageWithFiltersContentContainer>
      <PageWithFiltersContentContainer>
        <div style={{ display: 'flex', marginBottom: '1.5rem', justifyContent: 'flex-end', gap: '1rem' }}>
          <NewCurrencyExchangeBtn />
        </div>
        <Table
          dataSource={currencyExchange}
          scroll={{ x: true }}
          columns={columns}
          loading={isLoading}
          pagination={{
            total: pageInfo.total,
            defaultPageSize: 10,
            defaultCurrent: 1,
            onChange: (page, pageSize) => {
              setPaginationModel({ page, pageSize });
            },
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </PageWithFiltersContentContainer>
      <Modal
        title="Crear nuevo Currency Exchange"
        open={newCurrencyExchangeModalOpen}
        onCancel={() => setNewCurrencyExchangeModalOpen(false)}
        footer={null}
        width={490}
      >
        <NewCurrencyExchangeForm onCancel={() => setNewCurrencyExchangeModalOpen(false)} />
      </Modal>
    </PageWithFilters>
  );
};

export default withAuthorization(
  [RealmRoles.CurrencyExchangeAllRead, RealmRoles.CurrencyExchangeAllWrite],
  CurrencyExchange
);
